import { useState } from 'react';
import { useDirectory } from 'src/contexts/DirectoryContext';
import { MatchPatientBody } from 'src/shared/types';
import { apiPost } from 'src/utils/api';
import { Card } from '../Card';
import { UserIcon } from '@heroicons/react/24/outline';
import Input from '../inputs/Input';
import SelectInput from '../inputs/SelectInput';
import Button from '../Button';
import { ExclamationCircleIcon } from '@heroicons/react/24/solid';
import { Organization } from 'src/shared/types';

interface PatientInfoFormProps {
  onSubmit: (patientId: string) => void;
  onBack: () => void;
}

const PatientInfoForm = ({ onSubmit, onBack }: PatientInfoFormProps) => {
  const { organization } = useDirectory();
  const [ firstName, setFirstName ] = useState('');
  const [ lastName, setLastName ] = useState('');
  const [ gender, setGender ] = useState('male');
  const [ birthDate, setBirthDate ] = useState('');
  const [ phone, setPhone ] = useState('');
  const [ email, setEmail ] = useState('');
  const [ ssn, setSsn ] = useState('');
  const [ error, setError ] = useState('');
  const [ loading, setLoading ] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    const createPatient = async (organization: Organization, matchBody: MatchPatientBody) => {
      const createResponse = await apiPost(
        `/api/${organization.id}/ehr/create-patient`,
        matchBody
      );

      if (!createResponse.ok) {
        throw new Error('Failed to create patient');
      }

      const createData = await createResponse.json();
      onSubmit(createData.patientId);
    }
    
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!organization) {
        setError('Organization not found');
        return;
      }

      const matchBody: MatchPatientBody = {
        firstName,
        lastName,
        gender,
        birthDate,
        ssn,
      };
      if (phone) matchBody.phone = phone;
      if (email) matchBody.email = email;

      const response = await apiPost(
        `/api/${organization.id}/ehr/match-patient`,
        matchBody
      );

      if (!response.ok) {
        if (response.status === 404) {
          await createPatient(organization, matchBody);
          return;
        }

        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to match patient');
      }

      const data = await response.json();
      if (data.tempPatientId) {
        await createPatient(organization, matchBody);
        return;
      }
      onSubmit(data.patientId);
    } catch (err) {
      setError('An error occurred while processing your information. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Card className="p-8 shadow-xl rounded-2xl bg-white border-0">
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-brand-100 rounded-full flex items-center justify-center mb-4">
            <UserIcon className="w-8 h-8 text-brand-600" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Patient Information</h3>
          <p className="text-gray-600">Please provide your information to continue</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              type="text"
              label="First Name"
              placeholder="Enter your first name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required={true}
            />

            <Input
              type="text"
              label="Last Name"
              placeholder="Enter your last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required={true}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              required={false}
              type="tel"
              label="Home Phone"
              placeholder="123-456-7890"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />

            <Input
              required={false}
              type="email"
              label="Email Address"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              required={true}
              type="date"
              label="Date of Birth"
              placeholder="Enter your date of birth"
              value={birthDate}
              onChange={(e) => setBirthDate(e.target.value)}
            />

            <Input
              required={true}
              type="text"
              label="Social Security Number"
              placeholder="000-00-0000"
              value={ssn}
              onChange={(e) => setSsn(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <SelectInput
              label="Legal Sex"
              placeholder="Select legal sex"
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              options={[
                { value: 'male', label: 'Male' },
                { value: 'female', label: 'Female' }
              ]}
              required={true}
            />
          </div>

          {error && (
            <div className="flex items-start gap-2 p-4 bg-red-50 border border-red-200 rounded-lg">
              <ExclamationCircleIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <div className="flex gap-4 pt-4">
            <Button
              onClick={onBack}
              text="Back"
              className="flex-1 bg-gray-100 text-gray-700 hover:bg-gray-200"
            />
            <button
              type="submit"
              disabled={loading}
              className={`flex-1 py-3 px-6 rounded-lg text-base font-semibold transition-colors shadow-sm ${
                loading
                  ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                  : 'bg-gradient-to-r from-brand-500 to-brand-600 text-white hover:from-brand-600 hover:to-brand-700'
              }`}
            >
              {loading ? 'Processing...' : 'Continue'}
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default PatientInfoForm;
